/* data.tsx — the two data widgets more than one screen needs: the org lattice and the people list.
 *
 * Both were single functions in the old app used from four places each, and that is why they are
 * shared here rather than inlined per screen: the moment a second implementation of "how far is
 * this person from the plant" exists, the two disagree on some row nobody checks.
 */
import { useEffect, useState } from 'react';
import { api, isErr } from '../lib/api.ts';
import { LEVELS, N, ROLE_SHORT, ROLE_COLOR, distText, safeUrl } from '../lib/fmt.ts';
import { Chip, Copy, ErrBox, Hint, HAIR } from './ui.tsx';

export interface Cell { dept: string; level: number; n: number; reach: number }

const DEPT_ORDER = ['Executive', 'Operations', 'Manufacturing/Production', 'Engineering',
  'Maintenance/Reliability', 'Quality', 'Supply Chain/Procurement/Logistics', 'Facilities/EHS',
  'R&D', 'Software', 'IT/Systems', 'Sales', 'Marketing', 'Customer Success/Service',
  'Finance/Accounting', 'HR/People', 'Legal/Compliance', 'Admin', 'Other', '(untitled)', '(unmapped)'];

const ABBR: [string, string][] = [
  ['Manufacturing/Production', 'Mfg'], ['Supply Chain/Procurement/Logistics', 'Supply'],
  ['Maintenance/Reliability', 'Maint'], ['Customer Success/Service', 'CS'],
  ['Finance/Accounting', 'Fin'], ['Legal/Compliance', 'Legal'], ['Facilities/EHS', 'EHS'],
  ['IT/Systems', 'IT'], ['Engineering', 'Eng'], ['Executive', 'Exec'], ['Operations', 'Ops'],
  ['Marketing', 'Mktg'], ['(unmapped)', '?'], ['(untitled)', '—'],
];
const abbr = (d: string) => ABBR.reduce((s, [a, b]) => s.replace(a, b), d);

/* The org lattice: department × level, cell = headcount, colour = share reachable.
 *
 * A matrix with no handler must not LOOK clickable — no pointer cursor, no hover outline, no
 * "click a cell" invitation. A control that responds visually and does nothing is a defect, and the
 * Manager view passes no handler because there is nothing to open from there yet. */
export function Matrix({ cells, onCell }: { cells: Cell[]; onCell?: (dept: string, level: number) => void }) {
  const [sel, setSel] = useState<string | null>(null);
  const live = typeof onCell === 'function';
  if (!cells.length) return <Hint>No org lattice for this scope.</Hint>;

  const depts = [...new Set(cells.map((c) => c.dept))]
    .sort((a, b) => ((DEPT_ORDER.indexOf(a) + 1) || 99) - ((DEPT_ORDER.indexOf(b) + 1) || 99));
  const map: Record<string, Cell> = {};
  let max = 0;
  for (const c of cells) { map[c.dept + '|' + c.level] = c; max = Math.max(max, c.n); }
  const lv = LEVELS.filter(([l]) => cells.some((c) => c.level === l));

  /* Colour carries the SHARE REACHABLE, brightness carries the headcount — two facts in one cell,
     neither invented. Green over half, amber over a fifth, orange below. */
  const bg = (n: number, reach: number) => {
    if (!n) return undefined;
    const t = Math.min(1, Math.log(1 + n) / Math.log(1 + max));
    const rr = reach / n;
    const base = rr > 0.5 ? [47, 208, 123] : rr > 0.2 ? [253, 182, 20] : [224, 154, 0];
    return `rgba(${base.join(',')},${(0.18 + 0.8 * t).toFixed(2)})`;
  };

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 'var(--t-micro)' }}>
          <tbody>
            <tr>
              <th />
              {depts.map((d) => (
                <th key={d} title={d} style={{ padding: '4px 6px', color: 'var(--dim2)', fontWeight: 600, textAlign: 'center', whiteSpace: 'nowrap' }}>{abbr(d)}</th>
              ))}
            </tr>
            {lv.map(([l, nm]) => (
              <tr key={l}>
                <th style={{ padding: '3px 8px 3px 0', color: 'var(--dim)', fontWeight: 400, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {nm} <span className="num" style={{ color: 'var(--am)' }}>{l >= 0 ? l : ''}</span>
                </th>
                {depts.map((d) => {
                  const c = map[d + '|' + l];
                  const key = d + '|' + l;
                  if (!c) return <td key={d} style={{ border: HAIR }} />;
                  return (
                    <td
                      key={d}
                      onClick={live ? () => { setSel(key); onCell!(d, l); } : undefined}
                      title={`${d} · ${nm}: ${N(c.n)} people, ${N(c.reach)} reachable`}
                      className="num"
                      style={{
                        background: bg(c.n, c.reach), border: sel === key ? '1px solid var(--or)' : HAIR,
                        padding: '3px 7px', textAlign: 'right', minWidth: 34,
                        cursor: live ? 'pointer' : 'default', color: '#06070A', fontWeight: 600,
                      }}
                    >{N(c.n)}</td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Hint style={{ marginTop: 6 }}>
        cell = headcount · colour = share reachable (green &gt;50%, amber &gt;20%, orange below)
        {live ? ' · click a cell for the people' : ''}
      </Hint>
    </div>
  );
}

export interface Person {
  person_id: number; full_name: string; title: string | null; dept: string | null; level: number;
  icp_role: string | null; email: string | null; phone: string | null; switchboard: number | null;
  linkedin_url: string | null; facility_dist_mi: number | null; location_source: string | null;
}

/* The people list.
 *
 * The email and the phone number ARE the deliverable of this system, and they used to be a 10px ✉
 * and ☎ with no value behind them — reviewers could not find them on the row. So they are the
 * second line of every row now, selectable, and a click copies them. A person with neither says so
 * in words rather than showing an empty gap.
 *
 * `omit` drops one person: the work queue asks for "the REST of the committee in this building" and
 * the endpoint returns everyone at the plant, so the person whose dossier you are reading was
 * appearing as the first row of their own committee. */
export function PeopleList({ params, omit, onPick, cap = 200 }: {
  params: Record<string, string | number>; omit?: number | string;
  onPick?: (id: number) => void; cap?: number;
}) {
  const [rows, setRows] = useState<Person[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const key = JSON.stringify(params);

  useEffect(() => {
    let dead = false;
    setRows(null); setErr(null);
    const qsv = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)]));
    api<{ rows: Person[] }>('people?' + qsv).then((r) => {
      if (dead) return;
      if (isErr(r)) { setErr(r.error); return; }
      setRows(omit == null ? r.rows : r.rows.filter((p) => String(p.person_id) !== String(omit)));
    });
    return () => { dead = true; };
    // params is compared by value: a fresh object literal on every render would refetch forever.
  }, [key, omit]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <ErrBox what="The people list did not load" msg={err} />;
  if (!rows) return <Hint style={{ padding: 8 }}>loading…</Hint>;
  if (!rows.length) return <Hint style={{ padding: 8 }}>No reachable people match.</Hint>;

  /* ONE slice feeds both the rows and the footer count. They used to be computed separately, and
     "400 shown" sat over 200 rendered rows. */
  const shown = rows.slice(0, cap);
  return (
    <div>
      {shown.map((p) => {
        const lu = safeUrl(p.linkedin_url);
        const d = distText(p.facility_dist_mi, p.location_source);
        return (
          <div
            key={p.person_id}
            onClick={onPick ? () => onPick(p.person_id) : undefined}
            style={{ padding: '6px 8px', borderBottom: HAIR, cursor: onPick ? 'pointer' : 'default' }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0 }}>
              <span className="num" style={{ color: 'var(--dim2)', width: 16, fontSize: 'var(--t-micro)' }}>{p.level}</span>
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{p.full_name}</span>
              <span style={{ color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 auto', minWidth: 0 }}>{p.title || ''}</span>
              {d && <Chip tone={d.soft ? 'soft' : 'none'} title={d.why}>{d.text}</Chip>}
              <span style={{ color: ROLE_COLOR[p.icp_role || ''] || 'var(--dim2)', fontSize: 'var(--t-micro)', whiteSpace: 'nowrap' }}>
                {ROLE_SHORT[p.icp_role || ''] || ''}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 2, paddingLeft: 23, alignItems: 'baseline' }}>
              {p.email
                ? <Copy value={p.email} />
                : <span className="t-micro" style={{ color: 'var(--dim2)' }}>no email on file</span>}
              {p.phone
                ? <Copy value={p.phone} label={p.phone + (p.switchboard ? ' · switchboard' : '')} />
                : <span className="t-micro" style={{ color: 'var(--dim2)' }}>no phone</span>}
              {lu && <a href={lu} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}
                className="t-micro" style={{ color: 'var(--bl)' }}>in ↗</a>}
            </div>
          </div>
        );
      })}
      <Hint style={{ padding: 8 }}>
        {shown.length < rows.length
          ? `${N(shown.length)} shown of ${N(rows.length)} — narrow with the filters above`
          : `${N(shown.length)} shown`}
      </Hint>
    </div>
  );
}
